import type { CSSProperties } from 'react';

import { Box, Container, Stack, Text, Title } from '@mantine/core';

import { MarketingEyebrow } from '@/components/atoms';
import { useInView } from '@/hooks/useInView';
import { T } from '@/theme/assay';
import { TIER_MAP } from '@/types/grade';

// ─── Timing (seconds) ────────────────────────────────────────────────────────
// 0.0 seams draw in · 1.4 decay begins · ~3.2 field settles · 3.2-3.8 hold
// ("and you didn't notice") · 3.8 scan sweep · ~5.0 rest. Plays once.
const DRAW_STAGGER = 0.06;
const DRAW_DUR = 0.8;
const SCAN_START = 3.8;
const SCAN_DUR = 0.9;

const ROW_H = 30; // px per seam row
const BAR_H = 3;
const SCAN_TAIL = 22; // px glow tail above the scan line

const EASE_DRAW = 'cubic-bezier(0.22, 1, 0.36, 1)';
// Three drain curves, mixed across the field so the cooling reads uneven.
const EASE_A = 'cubic-bezier(0.6, 0.04, 0.35, 1)';
const EASE_B = 'cubic-bezier(0.45, 0.02, 0.2, 1)';
const EASE_C = 'cubic-bezier(0.7, 0.1, 0.45, 0.95)';

// Tier names come from TIER_MAP - never retype them (see grade.ts).
const INNER = TIER_MAP.contact_immediately.shortLabel;
const TRUST = TIER_MAP.high_trust.shortLabel;
const NETWORK = TIER_MAP.professional_network.shortLabel;
const ACQ = TIER_MAP.loose_connection.shortLabel;

interface Seam {
  /** Seam length as % of the field width */
  len: number;
  /** Warm layer's resting translateX (own-width %). Nearer -77 = fully cold. */
  decayX: number;
  /** The handful of seams that start hottest */
  bright?: boolean;
  drainDelay: number;
  drainDur: number;
  drainEase: string;
  /** Mono label - only genuinely decayed seams get one */
  label?: string;
}

// Hand-tuned and deliberately uneven. Two of the brightest seams (1, 9) end
// up the coldest - closeness doesn't protect anything.
const SEAMS: Seam[] = [
  { len: 92, decayX: -10, drainDelay: 1.48, drainDur: 1.1, drainEase: EASE_B },
  {
    len: 88,
    decayX: -66,
    bright: true,
    drainDelay: 1.4,
    drainDur: 1.55,
    drainEase: EASE_A,
    label: `${INNER} · 47 days`,
  },
  { len: 64, decayX: -18, drainDelay: 1.62, drainDur: 0.95, drainEase: EASE_C },
  { len: 97, decayX: -6, bright: true, drainDelay: 1.55, drainDur: 1.05, drainEase: EASE_B },
  {
    len: 78,
    decayX: -55,
    drainDelay: 1.44,
    drainDur: 1.35,
    drainEase: EASE_C,
    label: `${TRUST} · 3 months`,
  },
  { len: 58, decayX: -26, drainDelay: 1.7, drainDur: 1.15, drainEase: EASE_A },
  { len: 85, decayX: -13, drainDelay: 1.52, drainDur: 0.9, drainEase: EASE_B },
  {
    len: 70,
    decayX: -60,
    drainDelay: 1.46,
    drainDur: 1.6,
    drainEase: EASE_A,
    label: `${NETWORK} · 7 months`,
  },
  { len: 66, decayX: -22, drainDelay: 1.66, drainDur: 1.0, drainEase: EASE_C },
  {
    len: 94,
    decayX: -70,
    bright: true,
    drainDelay: 1.42,
    drainDur: 1.7,
    drainEase: EASE_A,
    label: `${INNER} · 62 days`,
  },
  { len: 82, decayX: -11, drainDelay: 1.58, drainDur: 0.95, drainEase: EASE_B },
  { len: 61, decayX: -32, drainDelay: 1.5, drainDur: 1.2, drainEase: EASE_C },
  {
    len: 55,
    decayX: -52,
    drainDelay: 1.64,
    drainDur: 1.25,
    drainEase: EASE_A,
    label: `${ACQ} · 11 months`,
  },
  { len: 90, decayX: -8, drainDelay: 1.54, drainDur: 1.0, drainEase: EASE_B },
];

const FIELD_H = SEAMS.length * ROW_H;
const HEADING_ID = 'decay-section-heading';

export function DecaySection() {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <Box
      component="section"
      ref={ref}
      aria-labelledby={HEADING_ID}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      <Container size="lg">
        <Stack gap={48}>
          <Stack gap={16} maw={680}>
            <MarketingEyebrow label="Relationship decay" inView={inView} />

            <Title
              id={HEADING_ID}
              order={2}
              style={{
                margin: 0,
                fontWeight: 300,
                fontSize: 'clamp(1.9rem, 3.4vw, 3rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.01em',
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.5s both' : undefined,
              }}
            >
              You never notice it happening.
            </Title>

            <Text
              size="sm"
              c="dimmed"
              style={{
                maxWidth: 560,
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.65s both' : undefined,
              }}
            >
              Every relationship cools at its own rate - inner circle included. Assay watches the
              whole field and flags the ones that go quiet.
            </Text>
          </Stack>

          {/* Seam field - pure decoration; the idea lives in the copy above. */}
          <Box aria-hidden style={{ position: 'relative', height: FIELD_H, overflow: 'hidden' }}>
            {SEAMS.map((s, i) => {
              const markDelay = SCAN_START + SCAN_DUR * ((i * ROW_H + ROW_H / 2) / FIELD_H);
              return (
                <Box
                  key={`${s.len}-${s.decayX}-${i}`}
                  style={{ position: 'absolute', left: 0, right: 0, top: i * ROW_H, height: ROW_H }}
                >
                  <Box
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: ROW_H / 2 - Math.floor(BAR_H / 2),
                      height: BAR_H,
                      width: `${s.len}%`,
                      overflow: 'hidden',
                      borderRadius: 1,
                      transformOrigin: 'left',
                      transform: inView ? undefined : 'scaleX(0)',
                      animation: inView
                        ? `assay-strata-line-draw ${DRAW_DUR}s ${EASE_DRAW} ${i * DRAW_STAGGER}s both`
                        : undefined,
                    }}
                  >
                    {/* Cold base - what remains once the warmth is gone */}
                    <Box
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(90deg, ${T.textMuted}, ${T.border})`,
                        opacity: 0.4,
                      }}
                    />
                    {/* Warm layer - drains right-to-left with a diffuse leading edge.
                        Width is 130% of the seam so the fade tail starts off-canvas
                        and the visible seam begins uniformly gold. */}
                    <Box
                      style={
                        {
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          width: '130%',
                          background: `linear-gradient(90deg, ${s.bright ? T.oreLight : T.ore} 0%, ${T.ore} 60%, ${T.ore} 74%, rgba(196, 152, 58, 0.4) 88%, rgba(196, 152, 58, 0) 100%)`,
                          '--decay-x': `${s.decayX}%`,
                          animation: inView
                            ? `assay-decay-drain ${s.drainDur}s ${s.drainEase} ${s.drainDelay}s both`
                            : undefined,
                        } as CSSProperties
                      }
                    />
                  </Box>

                  {s.label && (
                    <>
                      {/* Faint resting glow behind the labelled seam's left edge */}
                      <Box
                        style={{
                          position: 'absolute',
                          left: -8,
                          top: ROW_H / 2 - 10,
                          width: '30%',
                          height: 20,
                          background:
                            'radial-gradient(ellipse 60% 100% at 0% 50%, rgba(196, 152, 58, 0.16), transparent 70%)',
                          opacity: 0,
                          animation: inView
                            ? `assay-fade-in 0.6s ease ${markDelay}s both`
                            : undefined,
                        }}
                      />
                      {/* Gold marker pinned where the scan crossed */}
                      <Box
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: ROW_H / 2 - 7,
                          width: 2,
                          height: 12,
                          background: T.oreLight,
                          boxShadow: '0 0 12px rgba(196, 152, 58, 0.55)',
                          opacity: 0,
                          animation: inView
                            ? `assay-decay-mark-in 0.35s ease ${markDelay}s both`
                            : undefined,
                        }}
                      />
                      <Text
                        style={{
                          position: 'absolute',
                          left: 12,
                          top: 0,
                          fontFamily: '"DM Mono", monospace',
                          fontSize: '0.6rem',
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          lineHeight: 1,
                          color: T.textMuted,
                          opacity: 0,
                          animation: inView
                            ? `assay-decay-label-in 0.5s ease ${markDelay + 0.08}s both`
                            : undefined,
                        }}
                      >
                        {s.label}
                      </Text>
                    </>
                  )}
                </Box>
              );
            })}

            {/* Scan line - Assay noticing. Sweeps once, top to bottom, then exits. */}
            <Box
              style={
                {
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: -SCAN_TAIL,
                  height: SCAN_TAIL,
                  background:
                    'linear-gradient(180deg, rgba(232, 184, 75, 0) 0%, rgba(232, 184, 75, 0.16) 62%, rgba(232, 184, 75, 0.9) 98%, rgba(232, 184, 75, 1) 100%)',
                  opacity: 0,
                  '--decay-scan-travel': `${FIELD_H + SCAN_TAIL}px`,
                  animation: inView
                    ? `assay-decay-scan ${SCAN_DUR}s linear ${SCAN_START}s both`
                    : undefined,
                } as CSSProperties
              }
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
